/**
 * Jobber CRM Integration Form
 * 
 * Form for configuring Jobber CRM integration with OAuth setup and sync settings
 */

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ExternalLink, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateJobberIntegration } from '@/features/integrations/hooks/useIntegrations'
import { JobberIntegrationFormData, SyncFrequency } from '@/types/integrations'

const jobberFormSchema = z.object({
  name: z.string().min(1, 'Integration name is required'),
  client_id: z.string().min(1, 'Client ID is required'),
  client_secret: z.string().min(1, 'Client Secret is required'),
  franchise_region: z.string().min(1, 'Franchise region is required'),
  sync_customers: z.boolean().default(true),
  sync_jobs: z.boolean().default(true),
  sync_invoices: z.boolean().default(true),
  sync_estimates: z.boolean().default(true),
  auto_create_leads: z.boolean().default(true),
  sync_frequency: z.enum(['real_time', 'every_15_minutes', 'hourly', 'daily', 'weekly', 'manual']),
})

interface JobberIntegrationFormProps {
  onSuccess: () => void
  onCancel: () => void
}

const franchiseRegions = [
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'north_york', label: 'North York' },
  { value: 'london', label: 'London' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'HQ', label: 'Headquarters' },
]

const syncFrequencyOptions: Array<{ value: SyncFrequency; label: string }> = [
  { value: 'real_time', label: 'Real-time (recommended)' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'manual', label: 'Manual only' },
]

export function JobberIntegrationForm({ onSuccess, onCancel }: JobberIntegrationFormProps) {
  const [showClientSecret, setShowClientSecret] = useState(false)
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const createIntegration = useCreateJobberIntegration()

  const form = useForm<JobberIntegrationFormData>({
    resolver: zodResolver(jobberFormSchema),
    defaultValues: {
      name: 'Jobber CRM',
      sync_customers: true,
      sync_jobs: true,
      sync_invoices: true,
      sync_estimates: true,
      auto_create_leads: true,
      sync_frequency: 'hourly',
    },
  })

  const onSubmit = (data: JobberIntegrationFormData) => {
    createIntegration.mutate(data, {
      onSuccess: () => {
        onSuccess()
      },
    })
  }

  const testConnection = async () => {
    const values = form.getValues()
    if (!values.client_id || !values.client_secret) {
      form.setError('client_id', { message: 'Client ID and Secret are required for testing' })
      return
    }

    setIsTestingConnection(true)
    
    // Simulate API call to test connection
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      // In real implementation, this would make an actual API call to Jobber
      console.log('Testing Jobber connection...')
    } catch (error) {
      form.setError('client_secret', { message: 'Connection failed. Please check your credentials.' })
    } finally {
      setIsTestingConnection(false)
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Configuration</CardTitle>
          <CardDescription>
            Configure your Jobber CRM integration settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Integration Name</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder="Jobber CRM"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="franchise_region">Franchise Region</Label>
            <Select
              value={form.watch('franchise_region')}
              onValueChange={(value) => form.setValue('franchise_region', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select franchise region" />
              </SelectTrigger>
              <SelectContent>
                {franchiseRegions.map((region) => (
                  <SelectItem key={region.value} value={region.value}>
                    {region.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.franchise_region && (
              <p className="text-sm text-red-600">{form.formState.errors.franchise_region.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* OAuth Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>OAuth Credentials</CardTitle>
          <CardDescription>
            Enter your Jobber OAuth application credentials. 
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto ml-1"
              onClick={() => window.open('https://developer.getjobber.com', '_blank')}
            >
              Get credentials from Jobber Developer Console
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client_id">Client ID</Label>
            <Input
              id="client_id"
              {...form.register('client_id')}
              placeholder="Your Jobber OAuth Client ID"
            />
            {form.formState.errors.client_id && (
              <p className="text-sm text-red-600">{form.formState.errors.client_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_secret">Client Secret</Label>
            <div className="relative">
              <Input
                id="client_secret"
                type={showClientSecret ? 'text' : 'password'}
                {...form.register('client_secret')}
                placeholder="Your Jobber OAuth Client Secret"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowClientSecret(!showClientSecret)}
              >
                {showClientSecret ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </Button>
            </div>
            {form.formState.errors.client_secret && (
              <p className="text-sm text-red-600">{form.formState.errors.client_secret.message}</p>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={testConnection}
            disabled={isTestingConnection}
            className="w-full"
          >
            {isTestingConnection ? 'Testing Connection...' : 'Test Connection'}
          </Button>
        </CardContent>
      </Card>

      {/* Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Synchronization Settings</CardTitle>
          <CardDescription>
            Choose which data to synchronize between Jobber and your admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sync_customers"
                checked={form.watch('sync_customers')}
                onCheckedChange={(checked) => form.setValue('sync_customers', !!checked)}
              />
              <Label htmlFor="sync_customers" className="text-sm">
                Sync Customers
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sync_jobs"
                checked={form.watch('sync_jobs')}
                onCheckedChange={(checked) => form.setValue('sync_jobs', !!checked)}
              />
              <Label htmlFor="sync_jobs" className="text-sm">
                Sync Jobs
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sync_invoices"
                checked={form.watch('sync_invoices')}
                onCheckedChange={(checked) => form.setValue('sync_invoices', !!checked)}
              />
              <Label htmlFor="sync_invoices" className="text-sm">
                Sync Invoices
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sync_estimates"
                checked={form.watch('sync_estimates')}
                onCheckedChange={(checked) => form.setValue('sync_estimates', !!checked)}
              />
              <Label htmlFor="sync_estimates" className="text-sm">
                Sync Estimates
              </Label>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="auto_create_leads"
              checked={form.watch('auto_create_leads')}
              onCheckedChange={(checked) => form.setValue('auto_create_leads', !!checked)}
            />
            <Label htmlFor="auto_create_leads" className="text-sm">
              Automatically create leads from new Jobber customers
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sync_frequency">Sync Frequency</Label>
            <Select
              value={form.watch('sync_frequency')}
              onValueChange={(value) => form.setValue('sync_frequency', value as SyncFrequency)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sync frequency" />
              </SelectTrigger>
              <SelectContent>
                {syncFrequencyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Integration Features Info */}
      <Alert>
        <AlertDescription>
          <strong>What this integration will do:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            <li>Sync customer data from Jobber to your lead management system</li>
            <li>Track job statuses and completion in real-time</li>
            <li>Monitor invoice payments and outstanding amounts</li>
            <li>Automatically convert estimates to leads when approved</li>
            <li>Maintain two-way data synchronization for consistent records</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          type="submit" 
          disabled={createIntegration.isPending}
        >
          {createIntegration.isPending ? 'Creating Integration...' : 'Create Integration'}
        </Button>
      </div>
    </form>
  )
}